import * as React from 'react';
import { SessionProvider } from 'core/infrastructure';
import { Permission, PreparationOrderDTO, Res, TicketDTO } from 'core/api';
import { HStack, Flex, Tag as ChakraTag, Text, Span, VStack, Box } from '@chakra-ui/react';
import { Format } from 'core/ui';
import { SvgIcon } from 'core/ui-next';
import * as Images from 'pos/Images';
import { Command } from 'pos/common';
import * as SalesStore from '../SalesStore';
import { DateUtils } from 'core/utils';
import { Message } from 'core/ui-next/modal';
import { useClickWithFeedback } from 'pos/ui';

const baseTagProps = {
	paddingY: '1',
	gap: '1.5',
	bg: 'blue.800',
	boxShadow: 'none',
	border: '1px solid'
} as const;

function BaseTag({ onClick, cursor, ...rest }: React.ComponentProps<typeof ChakraTag.Root>) {
	return (
		<ChakraTag.Root
			{...baseTagProps}
			{...rest}
			{...useClickWithFeedback(onClick ?? (_ => { }))}
			cursor={cursor ?? (onClick ? 'pointer' : undefined)}
		/>
	);
}

function Tag({ icon, text, onClick }: { icon?: SvgIcon, text?: string, onClick?: () => void }) {
	const content = (
		<>
			{icon ? <ChakraTag.StartElement color={'white'}><SvgIcon h='4' w='4' src={icon} /></ChakraTag.StartElement> : null}
			{text ? <ChakraTag.Label fontSize={'sm'} fontWeight={'normal'} color={'white'} lineHeight={'normal'} truncate>{text}</ChakraTag.Label> : null}
		</>
	);

	return onClick
		? (
			<BaseTag
				onClick={onClick}
				maxW={{base: '48', lgOnly: '39', xlOnly: '42', '2xl': '72'}}
				h={'6'}
				paddingX={'1.5'}
				textOverflow={'ellipsis'}
				borderColor={{
					base: 'blue.800',
					_dark: 'neutral.800'
				}}
			>
				{content}
			</BaseTag>
		)
		: (
			<HStack justifyContent={'center'} color={'white'} maxW={{base: '48', lgOnly: '39', xlOnly: '42', '2xl': '72'}} h={'6'} textOverflow={'ellipsis'} gap={'1.5'} boxShadow={'none'}>
				{icon ? <SvgIcon h='4' w='4' src={icon} /> : null}
				{text ? <Text fontSize={'sm'} fontWeight={'normal'} lineHeight={'normal'} truncate>{text}</Text> : null}
			</HStack>
		);
}

function BusinessDayTag({ businessDay, onCloseDay }: { businessDay: Date, onCloseDay: () => Promise<void> }) {

	const showWarning = DateUtils.areSameDate(businessDay, DateUtils.today()) === false;

	const borderColor: React.ComponentProps<typeof ChakraTag.Root>['borderColor'] = showWarning ? 'warning' : {
		base: 'blue.800',
		_dark: 'neutral.800'
	};

	const onClick = React.useCallback(async () => {

		const message = showWarning
			? Res.Msg.MSG_CONFIRM_CLOSE_DAY_FROM_HEADER_WITH_WARNING()
			: Res.Msg.MSG_CONFIRM_CLOSE_DAY_FROM_HEADER_NO_WARNING();

		const iconColor = showWarning ? 'warning' : undefined;

		const result = await Message.confirm(message, {
			icon: showWarning ? Images.Warning : Images.CloseDay,
			iconColor: iconColor,
			btnOkLabel: Res.Web.HEADER_BUSINESS_DAY_CLOSE_DAY_BTN(),
			btnOkType: 'primary',
			btnCancelLabel: Res.Common.CANCEL_BTN()
		});

		if (result.ok === false)
			return;

		await onCloseDay();
	}, [onCloseDay, showWarning]);

	return <BaseTag
		display='flex'
		flexDir='column'
		onClick={onClick}
		paddingX={'2'}
		fontSize={'sm'}
		color={'white'}
		borderColor={borderColor}
	>
		<Text whiteSpace={'nowrap'}>{Res.Web.HEADER_BUSINESS_DAY_LBL()}</Text>
		<HStack gap={'1'}>
			{showWarning && <Box color='warning'><Images.Warning /></Box>}
			<Text>{Format.asDate(businessDay)}</Text>
		</HStack>
	</BaseTag>;
}

type PhoneHeaderContentProps = {
	ticket: TicketDTO;
	seatNumber: Nullable<number>;
	preparationOrder: Nullable<PreparationOrderDTO>;
	isShowTicketButton?: boolean;
	onBack?: () => void;
};

export function PhoneHeaderContent({ ticket, seatNumber, preparationOrder, isShowTicketButton = true, onBack }: PhoneHeaderContentProps) {

	const user = SessionProvider.isSessionStarted() ? SessionProvider.getCurrent().UserName : '';

	const onShowTicket = React.useCallback(async () => {
		await Command.execute('showCurrentTicket');
	}, []);

	const tags = [
		ticket.TableCompanions ? <Tag key="companions" icon={Images.TableCompanions} text={`${ticket.TableCompanions}`} /> : null,
		preparationOrder !== null ? <Tag key="prepOrder" icon={Images.PreparationOrder} text={preparationOrder.Name} /> : null,
		seatNumber !== null ? <Tag key="seat" text={`#${seatNumber}`} /> : null,
		ticket.HasOnlineReservation ? <Tag key="online" icon={Images.OnlineReservation} /> : null,
		ticket.PreparationsReady ? <Tag key="ready" icon={Images.ServePlate} /> : null,
	].filter(Boolean);

	return <HStack w={'full'} height={'full'} gap={'1'} justifyContent={'space-between'}>
		<VStack gap={'1.5'} alignItems={'flex-start'} lineHeight={'100%'} overflow={'hidden'}>
			<Flex maxW={'full'}>
				<Text
					fontSize={'sm'}
					fontWeight={'semibold'}
					truncate
				>
					{Format.asLocationDescription(ticket)} | {user}
				</Text>
			</Flex>
			{tags.length > 0 && (
				<HStack w={'full'} gap={'1.5'} height={'4.5'}>
					{tags.map((tag, idx) => (
						<React.Fragment key={idx}>
							{idx > 0 && <Span>|</Span>}
							{tag}
						</React.Fragment>
					))}
				</HStack>
			)}
		</VStack>
		<VStack w={'8'} h={'8'} justifyContent={'center'} alignItems={'center'}>
			{
				isShowTicketButton
					? <SvgIcon src={Images.ViewTicket} onClick={onShowTicket} />
					: <SvgIcon src={Images.ViewTicket} onClick={onBack ? onBack : () => {}}/>
			}
		</VStack>
	</HStack>;
}

type LargeDeviceHeaderContentProps = {
	ticket: TicketDTO,
	businessDay: Date,
	seatNumber: Nullable<number>;
	preparationOrder: Nullable<PreparationOrderDTO>;
	onBack: () => void;
	onCloseDay: () => Promise<void>
}

export function LargeDeviceHeaderContent({ ticket, businessDay, seatNumber, preparationOrder, onBack, onCloseDay }: LargeDeviceHeaderContentProps) {

	const onShowReservation = React.useCallback(async () => {
		await Command.execute('showCurrentReservation');
	}, []);

	const onChangeCustomer = React.useCallback(async () => {
		await Command.execute('setOrRemoveCustomer', {
			customer: ticket.Customer,
			setCustomer: SalesStore.setCustomer,
			requiredPermission: Permission.Ticket_SetBuyerCustomer
		});
	}, [ticket]);

	const onChangePriceList = React.useCallback(async () => {
		await Command.execute('setTicketPriceList');
	}, []);

	const onEditSeatNumber = React.useCallback(async () => {
		await Command.execute('editSeatNumber');
	}, []);

	const onChangePreparationOrder = React.useCallback(async () => {
		await Command.execute('changePreparationOrder');
	}, []);

	const onChangeTableCompanions = React.useCallback(async () => {
		await Command.execute('setTableCompanions');
	}, []);

	const onSetFriendlyName = React.useCallback(async () => {
		await Command.execute('setTicketFriendlyName', { setFriendlyName: SalesStore.setFriendlyName, currentFriendlyName: ticket.FriendlyName });
	}, [ticket]);


	return <HStack w='full' gap={'3.5'}>

		{/* Necesito pasarle un onCloseDay en lugar de usar directamente el command porque el SaleScreen realizar acciones que dependen
		    de su propio estado durante el proceso de cierre de día */}
		<BusinessDayTag businessDay={businessDay} onCloseDay={onCloseDay} />

		<VStack alignItems={'flex-start'}>
			<Flex gap={'3.5'}>
				<Tag icon={Images.SelectSaleLocation} text={Format.asLocationDescription(ticket)} onClick={onBack} />
				<Tag icon={Images.Shop} text={ticket.Customer.FiscalName ? ticket.Customer.FiscalName : Res.Web.CUSTOMER_BTN()} onClick={onChangeCustomer} />
				<Tag icon={Images.ConfigurePriceList} text={ticket.PriceList.Name} onClick={onChangePriceList} />
				<Tag icon={Images.TicketFriendlyName} text={ticket.FriendlyName ? ticket.FriendlyName : Res.Web.TICKET_FRIENDLY_NAME_BTN()} onClick={onSetFriendlyName} />
				{ticket.DraftSerialNumber.Number ? <Tag text={`${ticket.DraftSerialNumber.SerieName}-${ticket.DraftSerialNumber.Number}`} /> : null}
			</Flex>
			<Flex gap={'3.5'}>
				{ticket.HasOnlineReservation ? <Tag icon={Images.OnlineReservation} onClick={onShowReservation} /> : null}
				<Tag icon={Images.SeatNumber} text={seatNumber !== null ? Res.Web.SEAT_NUMBER_BTN(seatNumber) : Res.Web.EDIT_SEAT_NUMBER_BTN()} onClick={onEditSeatNumber} />
				<Tag icon={Images.TableCompanions} text={ticket.TableCompanions !== null ? Res.Web.TABLE_COMPANIONS_NUMBER_BTN(ticket.TableCompanions) : Res.Web.EDIT_TABLE_COMPANIONS_NUMBER_BTN()} onClick={onChangeTableCompanions} />
				<Tag icon={Images.PreparationOrder} text={preparationOrder ? preparationOrder.Name : Res.Web.PREPARATION_ORDER_BTN()} onClick={onChangePreparationOrder} />
				{ticket.PreparationsReady ? <Tag icon={Images.ServePlate} /> : null}
			</Flex>
		</VStack>
	</HStack>;
}
